// InvoiceDraftFilterForm.tsx
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import DateRangeComp from '../../../components/dateComponent/DateRangeComp';
import { API_DATE_FORMAT, formatDateForApi, formatDateRangeDisplay } from '../../../utils/dateLocale';
import SearchSelect from '../../../components/textFields/SearchSelect';
import TextField from '../../../components/textFields/TextField';
import { useGetAllUsersManagerAdminQuery, useGetAllRecordSourceLookupQuery } from '../../../services/GeneralApi';
import { constraintConfig } from '../../../constraintConfig';

interface InvoiceDraftFilterFormProps {
    onApplyFilters: (filters: any) => void;
    isApplyingFilters?: boolean;
    initialFilters?: any;
    showUserFilter?: boolean;
    /** When false, status is controlled by parent tabs (Receipt Vault style). */
    showStatusFilter?: boolean;
    /** When set, user filter is locked to this OrgUser / account id (self-only). */
    lockedUserId?: string;
}

const statusOptions = [
    { value: "0", label: "All Except Attach" },
    { value: "1", label: "Ready To Use" },
    { value: "2", label: "Failed" },
    { value: "3", label: "Attached" },
    { value: "4", label: "Linked Expense" },
    { value: "5", label: "Already Used" },
    { value: "6", label: "Future Receipt" },
    { value: "7", label: "CC Line Item" },
    { value: "9", label: "All" },
];

// Helper function to get status label
const getStatusLabel = (statusValue: string): string => {
    const found = statusOptions.find(option => option.value === statusValue);
    return found?.label || statusValue;
};

const InvoiceDraftFilterForm: React.FC<InvoiceDraftFilterFormProps> = ({
    onApplyFilters,
    isApplyingFilters = false,
    initialFilters = {},
    showUserFilter = true,
    showStatusFilter = true,
    lockedUserId,
}) => {
    const currentDate = moment().format(API_DATE_FORMAT);
    const threeDaysAgo = moment().subtract(7, 'days').format(API_DATE_FORMAT);
    const defaultStatus = String(constraintConfig.invoiceStatus.all); // All

    // Fetch users for dropdown
    const { data: getUserByManager } = useGetAllUsersManagerAdminQuery('', { skip: !showUserFilter });
    // Fetch record sources for dropdown
    const { data: recordSourceLookup } = useGetAllRecordSourceLookupQuery('');

    // Local filter states — use ?? so empty-string deep-link clears are preserved ("" || default is wrong)
    const [localFilterByStatus, setLocalFilterByStatus] = useState(initialFilters?.filterByStatus ?? defaultStatus);
    const [localFilterByStartDate, setLocalFilterByStartDate] = useState(
        initialFilters?.filterByStartDate !== undefined ? initialFilters.filterByStartDate : threeDaysAgo
    );
    const [localFilterByEndDate, setLocalFilterByEndDate] = useState(
        initialFilters?.filterByEndDate !== undefined ? initialFilters.filterByEndDate : currentDate
    );
    const [localFilterByUserIds, setLocalFilterByUserIds] = useState(
        lockedUserId || initialFilters?.filterByUserIds || ""
    );
    const [localSearchTerm, setLocalSearchTerm] = useState(initialFilters?.searchTerm || "");
    const [localFilterByItemStartDate, setLocalFilterByItemStartDate] = useState(initialFilters?.filterByItemStartDate || "");
    const [localFilterByItemEndDate, setLocalFilterByItemEndDate] = useState(initialFilters?.filterByItemEndDate || "");
    const [localFilterByItemAmount, setLocalFilterByItemAmount] = useState(initialFilters?.filterByItemAmount || "");
    const [localFilterByReceiptStartDate, setLocalFilterByReceiptStartDate] = useState(initialFilters?.filterByReceiptStartDate || "");
    const [localFilterByReceiptEndDate, setLocalFilterByReceiptEndDate] = useState(initialFilters?.filterByReceiptEndDate || "");
    const [localFilterByReceiptInvoiceNo, setLocalFilterByReceiptInvoiceNo] = useState(initialFilters?.filterByReceiptInvoiceNo || "");
    const [localFilterByReceiptAmount, setLocalFilterByReceiptAmount] = useState(initialFilters?.filterByReceiptAmount || "");
    const [localFilterByItemSource, setLocalFilterByItemSource] = useState(initialFilters?.filterByItemSource || 0);
    const [localFilterByReceiptSource, setLocalFilterByReceiptSource] = useState(initialFilters?.filterByReceiptSource || 0);
    const [localInvoiceDataDraftId, setLocalInvoiceDataDraftId] = useState(initialFilters?.invoiceDataDraftId || "");

    // Keep local controls in sync when parent deep-links / exclusive ID lookup clears filters
    useEffect(() => {
        if (!initialFilters) return;
        setLocalFilterByStatus(initialFilters.filterByStatus ?? defaultStatus);
        if (initialFilters.filterByStartDate !== undefined) {
            setLocalFilterByStartDate(initialFilters.filterByStartDate);
        }
        if (initialFilters.filterByEndDate !== undefined) {
            setLocalFilterByEndDate(initialFilters.filterByEndDate);
        }
        if (initialFilters.filterByUserIds !== undefined) {
            setLocalFilterByUserIds(lockedUserId || initialFilters.filterByUserIds || "");
        }
        if (initialFilters.searchTerm !== undefined) {
            setLocalSearchTerm(initialFilters.searchTerm);
        }
        if (initialFilters.invoiceDataDraftId !== undefined) {
            setLocalInvoiceDataDraftId(initialFilters.invoiceDataDraftId);
        }
        if (initialFilters.filterByItemStartDate !== undefined) {
            setLocalFilterByItemStartDate(initialFilters.filterByItemStartDate);
        }
        if (initialFilters.filterByItemEndDate !== undefined) {
            setLocalFilterByItemEndDate(initialFilters.filterByItemEndDate);
        }
        if (initialFilters.filterByReceiptStartDate !== undefined) {
            setLocalFilterByReceiptStartDate(initialFilters.filterByReceiptStartDate);
        }
        if (initialFilters.filterByReceiptEndDate !== undefined) {
            setLocalFilterByReceiptEndDate(initialFilters.filterByReceiptEndDate);
        }
        if (initialFilters.filterByItemAmount !== undefined) {
            setLocalFilterByItemAmount(initialFilters.filterByItemAmount);
        }
        if (initialFilters.filterByReceiptAmount !== undefined) {
            setLocalFilterByReceiptAmount(initialFilters.filterByReceiptAmount);
        }
        if (initialFilters.filterByReceiptInvoiceNo !== undefined) {
            setLocalFilterByReceiptInvoiceNo(initialFilters.filterByReceiptInvoiceNo);
        }
        if (initialFilters.filterByItemSource !== undefined) {
            setLocalFilterByItemSource(initialFilters.filterByItemSource);
        }
        if (initialFilters.filterByReceiptSource !== undefined) {
            setLocalFilterByReceiptSource(initialFilters.filterByReceiptSource);
        }
    }, [
        initialFilters?.filterByStatus,
        initialFilters?.filterByStartDate,
        initialFilters?.filterByEndDate,
        initialFilters?.filterByUserIds,
        initialFilters?.searchTerm,
        initialFilters?.invoiceDataDraftId,
        lockedUserId,
        defaultStatus,
    ]);

    // Handlers
    const handleStatusChange = (selectedOption: any) => {
        setLocalFilterByStatus(selectedOption.value);
    };

    const handleFilterByUser = (selectedOption: any) => {
        setLocalFilterByUserIds(selectedOption?.value || "");
    };

    const handleSearchChange = (e: any) => {
        setLocalSearchTerm(e.target.value);
    };

    const handleItemAmountChange = (e: any) => {
        setLocalFilterByItemAmount(e.target.value);
    };

    const handleReceiptInvoiceNoChange = (e: any) => {
        setLocalFilterByReceiptInvoiceNo(e.target.value);
    };

    const handleReceiptAmountChange = (e: any) => {
        setLocalFilterByReceiptAmount(e.target.value);
    };

    const handleInvoiceDataDraftIdChange = (e: any) => {
        setLocalInvoiceDataDraftId(e.target.value);
    };

    const handleItemSourceChange = (selectedOption: any) => {
        setLocalFilterByItemSource(selectedOption?.value || 0);
    };

    const handleReceiptSourceChange = (selectedOption: any) => {
        setLocalFilterByReceiptSource(selectedOption?.value || 0);
    };

    const handleDateRangeChange = (event: any, picker: any) => {
        setLocalFilterByStartDate(formatDateForApi(picker.startDate));
        setLocalFilterByEndDate(formatDateForApi(picker.endDate));
    };

    const handleCancelDateSelection = (event: any, picker: any) => {
        setLocalFilterByStartDate("");
        setLocalFilterByEndDate("");
    };

    const handleItemDateRangeChange = (event: any, picker: any) => {
        setLocalFilterByItemStartDate(formatDateForApi(picker.startDate));
        setLocalFilterByItemEndDate(formatDateForApi(picker.endDate));
    };

    const handleItemCancelDateSelection = (event: any, picker: any) => {
        setLocalFilterByItemStartDate("");
        setLocalFilterByItemEndDate("");
    };

    const handleReceiptDateRangeChange = (event: any, picker: any) => {
        setLocalFilterByReceiptStartDate(formatDateForApi(picker.startDate));
        setLocalFilterByReceiptEndDate(formatDateForApi(picker.endDate));
    };

    const handleReceiptCancelDateSelection = (event: any, picker: any) => {
        setLocalFilterByReceiptStartDate("");
        setLocalFilterByReceiptEndDate("");
    };

    const resolveUserIds = (value: string) => (lockedUserId ? lockedUserId : value || "");

    /** Clear every other criterion; keep only Tracking ID or SS Receipt ID (SS Receipt ID wins if both). */
    const buildExclusiveIdFilters = (trackingId: string, draftId: string) => {
        const cleared = {
            filterByStatus: defaultStatus,
            filterByStartDate: "",
            filterByEndDate: "",
            // Admins: clear user so support email deep-links / SS Receipt ID find any owner.
            // Self-only vault: keep locked user.
            filterByUserIds: lockedUserId || "",
            filterByItemStartDate: "",
            filterByItemEndDate: "",
            filterByItemAmount: "",
            filterByReceiptStartDate: "",
            filterByReceiptEndDate: "",
            filterByReceiptInvoiceNo: "",
            filterByReceiptAmount: "",
            filterByItemSource: 0,
            filterByReceiptSource: 0,
            searchTerm: "",
            invoiceDataDraftId: "",
        };
        if (draftId) {
            return { ...cleared, invoiceDataDraftId: draftId };
        }
        return { ...cleared, searchTerm: trackingId };
    };

    const syncLocalFromFilters = (filters: {
        filterByStatus: string;
        filterByStartDate: string;
        filterByEndDate: string;
        filterByUserIds: string;
        searchTerm: string;
        filterByItemStartDate: string;
        filterByItemEndDate: string;
        filterByItemAmount: string;
        filterByReceiptStartDate: string;
        filterByReceiptEndDate: string;
        filterByReceiptInvoiceNo: string;
        filterByReceiptAmount: string;
        filterByItemSource: number;
        filterByReceiptSource: number;
        invoiceDataDraftId: string;
    }) => {
        setLocalFilterByStatus(filters.filterByStatus);
        setLocalFilterByStartDate(filters.filterByStartDate);
        setLocalFilterByEndDate(filters.filterByEndDate);
        setLocalFilterByUserIds(filters.filterByUserIds);
        setLocalSearchTerm(filters.searchTerm);
        setLocalFilterByItemStartDate(filters.filterByItemStartDate);
        setLocalFilterByItemEndDate(filters.filterByItemEndDate);
        setLocalFilterByItemAmount(filters.filterByItemAmount);
        setLocalFilterByReceiptStartDate(filters.filterByReceiptStartDate);
        setLocalFilterByReceiptEndDate(filters.filterByReceiptEndDate);
        setLocalFilterByReceiptInvoiceNo(filters.filterByReceiptInvoiceNo);
        setLocalFilterByReceiptAmount(filters.filterByReceiptAmount);
        setLocalFilterByItemSource(filters.filterByItemSource);
        setLocalFilterByReceiptSource(filters.filterByReceiptSource);
        setLocalInvoiceDataDraftId(filters.invoiceDataDraftId);
    };

    const applyFilters = () => {
        const trackingId = (localSearchTerm || "").trim();
        const draftId = String(localInvoiceDataDraftId || "").trim();

        if (draftId || trackingId) {
            const filters = buildExclusiveIdFilters(trackingId, draftId);
            syncLocalFromFilters(filters);
            onApplyFilters(filters);
            return;
        }

        const filters = {
            filterByStatus: showStatusFilter
                ? (localFilterByStatus || defaultStatus)
                : (initialFilters?.filterByStatus ?? defaultStatus),
            filterByStartDate: localFilterByStartDate || "",
            filterByEndDate: localFilterByEndDate || "",
            filterByUserIds: resolveUserIds(localFilterByUserIds),
            searchTerm: "",
            filterByItemStartDate: localFilterByItemStartDate || "",
            filterByItemEndDate: localFilterByItemEndDate || "",
            filterByItemAmount: localFilterByItemAmount || "",
            filterByReceiptStartDate: localFilterByReceiptStartDate || "",
            filterByReceiptEndDate: localFilterByReceiptEndDate || "",
            filterByReceiptInvoiceNo: localFilterByReceiptInvoiceNo || "",
            filterByReceiptAmount: localFilterByReceiptAmount || "",
            filterByItemSource: localFilterByItemSource || 0,
            filterByReceiptSource: localFilterByReceiptSource || 0,
            invoiceDataDraftId: "",
        };
        onApplyFilters(filters);
    };

    const resetFilters = () => {
        setLocalFilterByStartDate(threeDaysAgo);
        setLocalFilterByEndDate(currentDate);
        setLocalFilterByUserIds(lockedUserId || "");
        setLocalFilterByStatus(defaultStatus);
        setLocalSearchTerm("");
        setLocalFilterByItemStartDate("");
        setLocalFilterByItemEndDate("");
        setLocalFilterByItemAmount("");
        setLocalFilterByReceiptStartDate("");
        setLocalFilterByReceiptEndDate("");
        setLocalFilterByReceiptInvoiceNo("");
        setLocalFilterByReceiptAmount("");
        setLocalFilterByItemSource(0);
        setLocalFilterByReceiptSource(0);
        setLocalInvoiceDataDraftId("");

        const filters = {
            filterByStatus: defaultStatus,
            filterByStartDate: threeDaysAgo,
            filterByEndDate: currentDate,
            filterByUserIds: lockedUserId || "",
            searchTerm: "",
            filterByItemStartDate: "",
            filterByItemEndDate: "",
            filterByItemAmount: "",
            filterByReceiptStartDate: "",
            filterByReceiptEndDate: "",
            filterByReceiptInvoiceNo: "",
            filterByReceiptAmount: "",
            filterByItemSource: 0,
            filterByReceiptSource: 0,
            invoiceDataDraftId: "",
        };
        onApplyFilters(filters);
    };

    return (
        <div className="row g-2 pt-1 ss-listing-filters filters-container">
            {/* Row 1 - First 4 filters */}
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <TextField
                    label='Expense Tracking ID:'
                    placeholder='Search by Expense Tracking ID'
                    value={localSearchTerm}
                    onChange={handleSearchChange}
                />
            </div>
            {showUserFilter && (
            <div className="col-md-4 col-lg-2 col-xl-2 filter-group">
                <SearchSelect
                    selectClass='filter-valueContainer'
                    label='User:'
                    options={[
                        ...(getUserByManager?.result || []).map((option: any) => ({
                            value: option.id,
                            label: option?.value,
                        })),
                    ]}
                    placeholder='Select User'
                    isClearable={true}
                    value={localFilterByUserIds && localFilterByUserIds !== ''
                        ? {
                            value: localFilterByUserIds,
                            label: getUserByManager?.result?.find((option: any) => option.id === localFilterByUserIds)?.value || localFilterByUserIds
                        }
                        : null
                    }
                    onChange={handleFilterByUser}
                />
            </div>
            )}
            {showStatusFilter && (
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <SearchSelect
                    selectClass='filter-valueContainer'
                    label='Receipt Status:'
                    options={statusOptions}
                    placeholder='Select Status'
                    value={
                        localFilterByStatus
                            ? {
                                value: localFilterByStatus,
                                label: getStatusLabel(localFilterByStatus)
                            }
                            : { value: defaultStatus, label: getStatusLabel(defaultStatus) }
                    }
                    onChange={handleStatusChange}
                />
            </div>
            )}
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <DateRangeComp
                    label={'Receipts Uploaded Date Range'}
                    startDate={localFilterByStartDate || ''}
                    endDate={localFilterByEndDate || ''}
                    onApply={handleDateRangeChange}
                    onCancel={handleCancelDateSelection}
                    value={
                        localFilterByStartDate && localFilterByEndDate
                            ? formatDateRangeDisplay(localFilterByStartDate, localFilterByEndDate)
                            : ''
                    }
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <TextField
                    label='Search by Receipt No:'
                    placeholder='Enter Receipt Number'
                    value={localFilterByReceiptInvoiceNo}
                    onChange={handleReceiptInvoiceNoChange}
                />
            </div>
            {/* Row 2 - Next 4 filters */}
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <DateRangeComp
                    label={'Item Date Date Range'}
                    startDate={localFilterByItemStartDate || ''}
                    endDate={localFilterByItemEndDate || ''}
                    onApply={handleItemDateRangeChange}
                    onCancel={handleItemCancelDateSelection}
                    value={
                        localFilterByItemStartDate && localFilterByItemEndDate
                            ? formatDateRangeDisplay(localFilterByItemStartDate, localFilterByItemEndDate)
                            : ''
                    }
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <DateRangeComp
                    label={'Receipt Date'}
                    startDate={localFilterByReceiptStartDate || ''}
                    endDate={localFilterByReceiptEndDate || ''}
                    onApply={handleReceiptDateRangeChange}
                    onCancel={handleReceiptCancelDateSelection}
                    value={
                        localFilterByReceiptStartDate && localFilterByReceiptEndDate
                            ? formatDateRangeDisplay(localFilterByReceiptStartDate, localFilterByReceiptEndDate)
                            : ''
                    }
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <TextField
                    label='Item Amount:'
                    placeholder='Enter Item Amount'
                    type="number"
                    value={localFilterByItemAmount}
                    onChange={handleItemAmountChange}
                />
            </div>
            {/* Row 3 - Remaining filters + action buttons */}
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <TextField
                    label='Receipt Amount:'
                    placeholder='Enter Receipt Amount'
                    type="number"
                    value={localFilterByReceiptAmount}
                    onChange={handleReceiptAmountChange}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <SearchSelect
                    selectClass='filter-valueContainer'
                    label='Item Source:'
                    options={[
                        ...(Array.isArray(recordSourceLookup?.result) ? recordSourceLookup.result.map((option: any) => ({
                            value: option.id,
                            label: option?.value,
                        })) : [])
                    ]}
                    placeholder='Select Item Source'
                    isClearable={true}
                    value={localFilterByItemSource && localFilterByItemSource !== 0
                        ? {
                            value: localFilterByItemSource,
                            label: recordSourceLookup?.result?.find((option: any) => option.id === localFilterByItemSource)?.value
                        }
                        : null
                    }
                    onChange={handleItemSourceChange}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <SearchSelect
                    selectClass='filter-valueContainer'
                    label='Receipt Uploaded From:'
                    options={
                        Array.isArray(recordSourceLookup?.result)
                            ? recordSourceLookup.result
                                .filter((option: any) => {
                                    const value = String(option?.value || '').toLowerCase();
                                    return value === 'webapp' || value === 'mobile';
                                })
                                .map((option: any) => ({
                                    value: option.id,
                                    label: option?.value,
                                }))
                            : []
                    }
                    placeholder='Select Receipt Source'
                    isClearable={true}
                    value={localFilterByReceiptSource && localFilterByReceiptSource !== 0
                        ? {
                            value: localFilterByReceiptSource,
                            label: recordSourceLookup?.result?.find((option: any) => option.id === localFilterByReceiptSource)?.value
                        }
                        : null
                    }
                    onChange={handleReceiptSourceChange}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 filter-group'>
                <TextField
                    label='SS Receipt ID'
                    placeholder='Enter SS Receipt ID'
                    value={localInvoiceDataDraftId}
                    onChange={handleInvoiceDataDraftIdChange}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 ss-listing-filter-actions-col filter-group filter-actions'>
                <div className='d-flex gap-2 ss-listing-filter-actions'>
                    <button type='button' className='btn btn-sm btn-danger ss-smart-btn text-nowrap' onClick={resetFilters}>Reset Filters</button>
                    <button type='button' className='btn btn-sm btn-primary ss-smart-btn text-nowrap' onClick={applyFilters}>
                        {isApplyingFilters ? <>
                            <span>loading...</span>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        </> : "Apply Filters"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvoiceDraftFilterForm;
